import type { Service } from "@/types/content";

export const services: Service[] = [
  {
    slug: "spine-disorders",
    title: "Spine Disorders",
    category: "spine",
    icon: "ri-body-scan-line",
    summary:
      "Assessment and treatment of the full range of spine conditions, from disc problems to complex and revision spine surgery.",
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20modern%20medical%20still%20life%20of%20a%20detailed%20human%20spine%20model%20beside%20a%20spine%20X-ray%20on%20a%20clean%20white%20desk%20in%20a%20premium%20orthopaedic%20clinic%2C%20soft%20royal%20blue%20and%20cyan%20lighting%2C%20clean%20minimal%20background%2C%20high%20detail%20editorial%20photography&width=1000&height=750&seq=ospc-svc-spine-2026&orientation=landscape",
    imageAlt: "Spine disorder treatment at Ortho Spine Care in Trichy",
    overview:
      "Spine disorders can affect the neck or lower back and may cause pain, numbness, weakness or difficulty walking. Our approach begins with a careful clinical assessment and appropriate imaging to identify the exact cause, followed by a treatment plan that considers non-surgical options first wherever possible.",
    highlights: [
      "Cervical disc prolapse",
      "Lumbar disc prolapse",
      "Spine trauma",
      "Spondylolisthesis",
      "Spine infections",
      "Spine deformity",
      "Spinal stenosis",
      "Spine tumour",
    ],
    treatments: [
      "Clinical assessment and imaging review",
      "Conservative management and physiotherapy guidance",
      "Minimally invasive spine surgery",
      "Complex and revision spine surgery",
    ],
    faqs: [
      {
        q: "Do all spine problems need surgery?",
        a: "No. Many spine conditions improve with non-surgical treatment such as physiotherapy, medication and activity modification. Surgery is considered when symptoms are severe, do not improve with conservative care, or involve progressive nerve weakness.",
      },
      {
        q: "When should I seek urgent spine care?",
        a: "Seek urgent attention for loss of bladder or bowel control, progressive weakness in the legs, or numbness around the groin area. These can indicate a condition that needs prompt specialist assessment.",
      },
    ],
  },
  {
    slug: "joint-disorders",
    title: "Joint Disorders",
    category: "orthopaedic",
    icon: "ri-focus-2-line",
    summary:
      "Joint replacement for arthritis and arthroscopy for ligament injuries of the knee and shoulder.",
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20anatomical%20knee%20and%20shoulder%20joint%20models%20arranged%20on%20a%20clean%20white%20surface%20in%20a%20modern%20orthopaedic%20clinic%2C%20soft%20royal%20blue%20and%20violet%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography&width=1000&height=750&seq=ospc-svc-joint-2026&orientation=landscape",
    imageAlt: "Joint disorder and joint replacement services in Trichy",
    overview:
      "Joint pain and stiffness can limit everyday movement and quality of life. We assess the cause of the problem — whether arthritis, injury or another condition — and discuss options that range from activity changes and physiotherapy to arthroscopy and joint replacement when appropriate.",
    highlights: [
      "Joint replacement surgeries for arthritis",
      "Arthroscopy for joint ligament injuries",
      "Knee and shoulder joint assessment",
      "Post-operative rehabilitation guidance",
    ],
    treatments: [
      "Clinical examination and imaging",
      "Non-surgical pain management",
      "Arthroscopic (keyhole) surgery",
      "Joint replacement surgery",
    ],
    faqs: [
      {
        q: "When is joint replacement recommended?",
        a: "Joint replacement is usually considered when pain from advanced arthritis significantly limits daily activities and has not responded to other treatment options. Your specialist will discuss the benefits and risks for your specific situation.",
      },
    ],
  },
  {
    slug: "tendon-disorders",
    title: "Tendon Disorders",
    category: "orthopaedic",
    icon: "ri-hand-heart-line",
    summary:
      "Care for rotator cuff injuries of the shoulder, and wrist, hand and heel tendon injuries.",
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20close%20up%20of%20anatomical%20models%20of%20the%20shoulder%20rotator%20cuff%20and%20ankle%20tendon%20on%20a%20clean%20white%20desk%20in%20a%20bright%20clinic%2C%20soft%20teal%20and%20cyan%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography&width=1000&height=750&seq=ospc-svc-tendon-2026&orientation=landscape",
    imageAlt: "Tendon disorder treatment including rotator cuff and heel tendon injuries",
    overview:
      "Tendons connect muscle to bone, and injury or degeneration can cause pain and loss of function. We evaluate the extent of the injury and recommend treatment that may range from rest and rehabilitation to surgical repair where needed.",
    highlights: [
      "Rotator cuff injuries of the shoulder",
      "Wrist and hand tendon injuries",
      "Heel tendon injuries",
      "Tendon repair and rehabilitation",
    ],
    treatments: [
      "Clinical assessment and imaging",
      "Physiotherapy and rehabilitation",
      "Tendon repair surgery where indicated",
    ],
    faqs: [
      {
        q: "Can tendon injuries heal without surgery?",
        a: "Many partial tendon injuries improve with rest, physiotherapy and a graded return to activity. Complete tears or injuries that do not heal may need surgical repair. Your specialist will advise based on your assessment.",
      },
    ],
  },
  {
    slug: "bone-deformities",
    title: "Bone Deformities",
    category: "orthopaedic",
    icon: "ri-heart-add-line",
    summary:
      "Quality diagnosis, treatment and management of bone deformities, delivered with experienced and compassionate care.",
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20anatomical%20skeletal%20leg%20and%20bone%20alignment%20model%20on%20a%20clean%20white%20surface%20in%20a%20modern%20orthopaedic%20consulting%20room%2C%20soft%20navy%20and%20cyan%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography&width=1000&height=750&seq=ospc-svc-bonedeformity-2026&orientation=landscape",
    imageAlt: "Bone deformity diagnosis and treatment",
    overview:
      "Bone deformities can affect alignment, function and confidence. We assess the underlying cause, monitor how the condition affects daily life, and plan treatment that may include observation, physiotherapy or corrective surgery depending on the situation.",
    highlights: [
      "Alignment and deformity assessment",
      "Monitoring and follow-up care",
      "Corrective treatment planning",
    ],
    treatments: ["Clinical and imaging assessment", "Physiotherapy guidance", "Corrective surgery where appropriate"],
    faqs: [
      {
        q: "Are all bone deformities treated with surgery?",
        a: "No. Many deformities are monitored or managed with physiotherapy. Surgery is considered when the deformity affects function, causes pain, or is likely to worsen without intervention.",
      },
    ],
  },
  {
    slug: "bone-tumours",
    title: "Bone Tumours",
    category: "orthopaedic",
    icon: "ri-microscope-line",
    summary:
      "Personalised, trusted care for bone tumours, with an experienced team committed to finding the best possible solution.",
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20bone%20scan%20and%20MRI%20images%20displayed%20on%20a%20modern%20lightbox%20in%20a%20clean%20dark%20radiology%20room%2C%20soft%20violet%20and%20blue%20glow%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography&width=1000&height=750&seq=ospc-svc-bonetumour-2026&orientation=landscape",
    imageAlt: "Bone tumour evaluation and treatment",
    overview:
      "Bone tumours may be benign or malignant and require careful evaluation. We coordinate appropriate imaging and, where needed, biopsy to reach a clear diagnosis before discussing treatment options with you.",
    highlights: [
      "Detailed clinical evaluation",
      "Imaging and diagnostic coordination",
      "Personalised treatment planning",
    ],
    treatments: ["Assessment and imaging", "Diagnostic confirmation", "Surgical treatment where indicated"],
    faqs: [
      {
        q: "Do all bone tumours need surgery?",
        a: "No. Treatment depends on the type of tumour and how it affects the bone. Some require monitoring while others need surgery. A clear diagnosis is the first step in planning the right approach.",
      },
    ],
  },
  {
    slug: "bone-infections",
    title: "Bone Infections",
    category: "orthopaedic",
    icon: "ri-shield-cross-line",
    summary:
      "Relief from bone infections, focused on controlling the infection and restoring bone health.",
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20close%20up%20of%20a%20lower%20leg%20bone%20model%20with%20a%20clean%20medical%20dressing%20and%20medication%20vial%20on%20a%20white%20surface%20in%20a%20bright%20clinic%2C%20soft%20teal%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography&width=1000&height=750&seq=ospc-svc-boneinfection-2026&orientation=landscape",
    imageAlt: "Bone infection treatment and bone health restoration",
    overview:
      "Bone infections can be painful and, if untreated, cause lasting damage. Treatment focuses on controlling the infection with the appropriate medication and, where necessary, surgical cleaning of the affected bone.",
    highlights: [
      "Prompt diagnosis of bone infection",
      "Medical management of infection",
      "Surgical debridement where required",
    ],
    treatments: ["Clinical assessment and tests", "Antibiotic treatment", "Surgical cleaning where indicated"],
    faqs: [
      {
        q: "How is a bone infection diagnosed?",
        a: "Diagnosis usually combines clinical examination, blood tests and imaging. Sometimes a sample is taken to identify the organism so the correct treatment can be chosen.",
      },
    ],
  },
  {
    slug: "complex-bone-fracture-management",
    title: "Complex Bone Fracture Management",
    category: "orthopaedic",
    icon: "ri-first-aid-kit-line",
    summary:
      "Expert treatment for complex bone fractures, with a focus on safe and successful recovery.",
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20X-ray%20of%20a%20fractured%20long%20bone%20displayed%20on%20a%20clean%20lightbox%20in%20a%20modern%20orthopaedic%20clinic%2C%20soft%20blue%20and%20coral%20accent%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography&width=1000&height=750&seq=ospc-svc-fracture-2026&orientation=landscape",
    imageAlt: "Complex bone fracture management and fixation",
    overview:
      "Complex fractures involve multiple bone fragments, involve a joint, or occur with other injuries. These need careful planning and skilled fixation to restore alignment and support healing.",
    highlights: [
      "Complex fracture assessment",
      "Surgical fixation and stabilisation",
      "Rehabilitation and recovery support",
    ],
    treatments: ["Imaging and fracture assessment", "Surgical fixation", "Post-operative rehabilitation"],
    faqs: [
      {
        q: "How long does a fracture take to heal?",
        a: "Healing time varies with the bone involved, the severity of the fracture and your general health. Your specialist will monitor progress and guide your recovery and return to activity.",
      },
    ],
  },
];